import type { ChipTone } from "@outpave/ds";
import dayjs, { parseIsoOrUsDate } from "utils/dayjs";
import { InvoiceStatus } from "enums/InvoiceStatus";
import DisputeStatus from "enums/DisputeStatus";

/**
 * What an invoice shows as its status: the enum, plus the dispute states and
 * two more that are derived at render rather than stored.
 */
export type InvoiceDisplayStatus =
  InvoiceStatus | DisputeStatus | "OVERDUE" | "AWAITING_VERIFICATION";

/**
 * The one place an invoice status becomes a label and a tone.
 *
 * Same shape as paymentStatus (OUT-1412) and apiKeyStatus (OUT-1416). The
 * estimate labels say which estimate, because "Approved" beside "Estimate Sent"
 * in one column does not say what was approved.
 */
const INVOICE_STATUS_LABEL_KEY: Record<InvoiceDisplayStatus, string> = {
  [InvoiceStatus.DRAFT]: "Draft",
  [InvoiceStatus.PENDING]: "Pending Approval",
  [InvoiceStatus.ESTIMATE]: "Estimate Sent",
  [InvoiceStatus.ESTIMATE_APPROVED]: "Estimate Approved",
  [InvoiceStatus.ESTIMATE_REJECTED]: "Estimate Rejected",
  [InvoiceStatus.SENT]: "Sent",
  [InvoiceStatus.PROCESSING_PAYMENT]: "Processing Payment",
  [InvoiceStatus.PAYMENT_FAILED]: "Payment Failed",
  [InvoiceStatus.PAID]: "Paid",
  [InvoiceStatus.COMPLETE]: "Complete",
  [InvoiceStatus.CANCELED]: "Canceled",
  [DisputeStatus.SUPPORT_REQUESTED]: "Support requested",
  [DisputeStatus.INFORMATION_REQUIRED]: "More information required",
  [DisputeStatus.UNDER_REVIEW]: "Dispute under review",
  [DisputeStatus.EXPIRED]: "Dispute expired",
  [DisputeStatus.WITHDRAWN]: "Dispute withdrawn",
  [DisputeStatus.DISPUTE_CANCELED]: "Dispute canceled",
  [DisputeStatus.CREATED]: "Disputed",
  [DisputeStatus.SUBMITTED]: "Disputed",
  [DisputeStatus.WON]: "Disputed",
  [DisputeStatus.LOST]: "Disputed",
  OVERDUE: "Overdue",
  AWAITING_VERIFICATION: "Awaiting Verification",
};

/**
 * Tone stands for the state's meaning, not its mood: `positive` for a settled
 * end state, `negative` for money that did not arrive or a refusal, `warning`
 * for something blocked on a person, `info` for work the system is doing on its
 * own, `neutral` for a terminal non-event that is nobody's problem.
 *
 * Six of these used to share one MUI `warning`, which is a component with no
 * tones rather than a deliberate palette. The four dispute tones are pinned to
 * RenderTransactionStatus's, so one dispute never prints two colours on the two
 * screens that draw it.
 */
const INVOICE_STATUS_TONE: Record<InvoiceDisplayStatus, ChipTone> = {
  [InvoiceStatus.DRAFT]: "neutral",
  [InvoiceStatus.PENDING]: "warning",
  [InvoiceStatus.ESTIMATE]: "info",
  [InvoiceStatus.ESTIMATE_APPROVED]: "positive",
  [InvoiceStatus.ESTIMATE_REJECTED]: "negative",
  [InvoiceStatus.SENT]: "info",
  [InvoiceStatus.PROCESSING_PAYMENT]: "info",
  [InvoiceStatus.PAYMENT_FAILED]: "negative",
  [InvoiceStatus.PAID]: "positive",
  [InvoiceStatus.COMPLETE]: "positive",
  [InvoiceStatus.CANCELED]: "neutral",
  [DisputeStatus.SUPPORT_REQUESTED]: "warning",
  [DisputeStatus.INFORMATION_REQUIRED]: "warning",
  [DisputeStatus.UNDER_REVIEW]: "info",
  [DisputeStatus.EXPIRED]: "neutral",
  [DisputeStatus.WITHDRAWN]: "neutral",
  [DisputeStatus.DISPUTE_CANCELED]: "neutral",
  [DisputeStatus.CREATED]: "warning",
  [DisputeStatus.SUBMITTED]: "warning",
  [DisputeStatus.WON]: "neutral",
  [DisputeStatus.LOST]: "negative",
  OVERDUE: "negative",
  AWAITING_VERIFICATION: "warning",
};

/**
 * Reads the shapes its callers pass: the drawer passes the epoch millis the
 * query returns (`dueDate` is a GraphQL Float), the table the display string
 * its row mapper already formatted, and an ISO string reads too.
 */
export const isInvoiceOverdue = (
  dueDate: Date | string | number | null | undefined,
): boolean =>
  !!dueDate && dayjs().isAfter(parseIsoOrUsDate(dueDate).endOf("day"));

interface InvoiceDisplayStatusArgs {
  status: InvoiceStatus | string | null | undefined;
  disputeStatus?: DisputeStatus | string | null;
  dueDate?: Date | string | number | null;
  microdepositPending?: boolean;
}

/**
 * An invoice shows what it is actually waiting on. A dispute outranks
 * everything, including a paid invoice: the money is contested either way.
 */
export const invoiceDisplayStatus = ({
  status,
  disputeStatus,
  dueDate,
  microdepositPending,
}: InvoiceDisplayStatusArgs): InvoiceDisplayStatus | undefined => {
  if (disputeStatus && disputeStatus in INVOICE_STATUS_LABEL_KEY) {
    return disputeStatus as InvoiceDisplayStatus;
  }

  if (!status) return undefined;

  if (microdepositPending && status === InvoiceStatus.PROCESSING_PAYMENT) {
    return "AWAITING_VERIFICATION";
  }

  if (status === InvoiceStatus.SENT && isInvoiceOverdue(dueDate)) {
    return "OVERDUE";
  }

  return status in INVOICE_STATUS_LABEL_KEY
    ? (status as InvoiceDisplayStatus)
    : undefined;
};

/** A translation KEY, so the caller owns the `t()` and no stored value reaches one. */
export const invoiceStatusLabelKey = (
  status: InvoiceDisplayStatus | string | null | undefined,
): string =>
  (status && INVOICE_STATUS_LABEL_KEY[status as InvoiceDisplayStatus]) ||
  "Unknown Status";

/** Fall-through is `neutral`: a status this map has not heard of is unknown, not failed. */
export const invoiceStatusTone = (
  status: InvoiceDisplayStatus | string | null | undefined,
): ChipTone =>
  (status && INVOICE_STATUS_TONE[status as InvoiceDisplayStatus]) || "neutral";
